import { gql, ApolloError } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import { Placar, SetPlacar } from '../types/placar';
import { FaseGrupoPartidas as FaseGrupoPartidasType } from '../types/graphql';

export const FASES_GRUPO_PARTIDAS_QUERY = gql`
  query FasesGrupoPartidas($faseCategoriaTorneioId: ID!) {
    fasesGrupoPartidas(faseCategoriaTorneioId: $faseCategoriaTorneioId) {
      id
      nome
      ordem
      equipes {
        id
        nome
        atletas {
          id
          usuario {
            id
            imagemUrl
            nome
          }
        }
      }
      partidas {
        id
        criadoEm
        atualizadoEm
        data
        numeroPartida
        status
        local {
          id
          nome
          urlLocalizacao
        }
        equipeA {
          id
          nome
          cidade
          atletas {
            id
            usuario {
              id
              nome
              username
              imagemUrl
            }
          }
        }
        equipeB {
          id
          nome
          cidade
          atletas {
            id
            usuario {
              id
              nome
              username
              imagemUrl
            }
          }
        }
        participantesA {
          id
          usuario {
            id
            nome
            username
            imagemUrl
          }
        }
        participantesB {
          id
          usuario {
            id
            nome
            username
            imagemUrl
          }
        }
        placar {
          id
          resultadoFinalA
          resultadoFinalB
          sets {
            gamesA
            gamesB
            numeroSet
            tiebreakA
            tiebreakB
          }
        }
        equipeAWO
        equipeBWO
        equipeADesistencia
        equipeBDesistencia
        equipeVencedora
      }
    }
  }
`;

interface FasesGrupoPartidasData {
  fasesGrupoPartidas: FaseGrupoPartidasType[];
}

interface FasesGrupoPartidasVars {
  faseCategoriaTorneioId: string;
}

export async function fetchFasesGrupoPartidasByFaseCategoriaTorneioId(
  faseCategoriaTorneioId: string,
): Promise<FaseGrupoPartidasType[]> {
  try {
    const { data } = await apolloClient.query<FasesGrupoPartidasData, FasesGrupoPartidasVars>({
      query: FASES_GRUPO_PARTIDAS_QUERY,
      variables: { faseCategoriaTorneioId },
      fetchPolicy: 'network-only',
    });
    return data?.fasesGrupoPartidas ?? [];
  } catch (error) {
    if (error instanceof ApolloError) {
      throw new Error(`Nao foi possivel carregar as partidas da fase. ${error.message}`);
    }
    throw error;
  }
}

export type FaseGrupoPartidas = FaseGrupoPartidasType;

export const FASE_GRUPO_PARTIDAS_POR_FASE_CATEGORIA_TORNEIO = gql`
  query FaseGrupoPartidasPorFaseCategoriaTorneio($faseCategoriaTorneioId: ID!) {
    faseGrupoPartidasPorFaseCategoriaTorneio(faseCategoriaTorneioId: $faseCategoriaTorneioId) {
      id
      nome
      ordem
      partidas {
        atualizadoEm
        criadoEm
        data
        id
        numeroPartida
        numeroSets
        gamesPorSets
        tiebreaks
        local {
          id
          nome
          urlLocalizacao
        }
        status
        equipeA {
          id
          nome
          atletas {
            id
            usuario {
              id
              nome
              username
              imagemUrl
            }
          }
        }
        equipeB {
          id
          nome
          atletas {
            id
            usuario {
              id
              imagemUrl
              nome
              username
            }
          }
        }
        participantesA {
          id
          usuario {
            id
            nome
            username
            imagemUrl
          }
        }
        participantesB {
          id
          usuario {
            id
            nome
            username
            imagemUrl
          }
        }
        placar {
          id
          resultadoFinalA
          resultadoFinalB
          sets {
            gamesA
            gamesB
            numeroSet
            tiebreakA
            tiebreakB
          }
        }
        equipeAWO
        equipeBWO
        equipeADesistencia
        equipeBDesistencia
        equipeVencedora
      }
    }
  }
`;

export interface UsuarioLite {
  id: string;
  nome: string;
  username: string;
  imagemUrl: string | null;
}

export interface AtletaLite {
  id: string;
  usuario: UsuarioLite;
}

export interface EquipeLite {
  id: string;
  nome: string;
  atletas: AtletaLite[];
}

export type SetPlacarLite = SetPlacar;

export interface PlacarLite extends Placar {
  id: string;
}

export interface ParticipanteLite {
  id: string;
  usuario?: {
    id: string;
    nome?: string;
    imagemUrl?: string;
    username?: string;
  };
}

export interface PartidaLite {
  id: string;
  numeroPartida?: number | null;
  data: string;
  local?: { id: string; nome: string; urlLocalizacao?: string | null } | null;
  equipeA: EquipeLite | null;
  equipeB: EquipeLite | null;
  participantesA?: ParticipanteLite[];
  participantesB?: ParticipanteLite[];
  placar: PlacarLite | null;
  status: string;
  atualizadoEm: string;
  criadoEm: string;
  equipeAWO?: boolean | null;
  equipeBWO?: boolean | null;
  equipeADesistencia?: boolean | null;
  equipeBDesistencia?: boolean | null;
  equipeVencedora?: string | null;
  numeroSets?: number | null;
  gamesPorSets?: number | null;
  tiebreaks?: number | null;
}

export interface FaseGrupoPartidasLite {
  id: string;
  nome: string;
  ordem?: number | null;
  partidas: PartidaLite[];
}

export interface FaseGrupoPartidasPorFaseCategoriaTorneioData {
  faseGrupoPartidasPorFaseCategoriaTorneio: FaseGrupoPartidasLite[];
}

export interface FaseGrupoPartidasPorFaseCategoriaTorneioVars {
  faseCategoriaTorneioId: string;
}

export async function buscarFaseGrupoPartidasPorFaseCategoriaTorneio(
  faseCategoriaTorneioId: string,
): Promise<FaseGrupoPartidasLite[]> {
  try {
    const { data } = await apolloClient.query<
      FaseGrupoPartidasPorFaseCategoriaTorneioData,
      FaseGrupoPartidasPorFaseCategoriaTorneioVars
    >({
      query: FASE_GRUPO_PARTIDAS_POR_FASE_CATEGORIA_TORNEIO,
      variables: { faseCategoriaTorneioId },
      fetchPolicy: 'network-only',
    });
    return data?.faseGrupoPartidasPorFaseCategoriaTorneio ?? [];
  } catch (error) {
    if (error instanceof ApolloError) {
      throw new Error(
        `Erro ao buscar FaseGrupoPartidas por FaseCategoriaTorneio: ${error.message}`,
      );
    }
    throw error;
  }
}

export const CRIAR_FASE_GRUPO_PARTIDAS = gql`
  mutation CriarFaseGrupoPartidas($input: FaseGrupoPartidasInput!) {
    criarFaseGrupoPartidas(input: $input) {
      id
    }
  }
`;

export const ATUALIZAR_FASE_GRUPO_PARTIDAS = gql`
  mutation AtualizarFaseGrupoPartidas($id: ID!, $input: FaseGrupoPartidasInput!) {
    atualizarFaseGrupoPartidas(id: $id, input: $input) {
      id
    }
  }
`;

export const DELETAR_FASE_GRUPO_PARTIDAS = gql`
  mutation DeletarFaseGrupoPartidas($id: ID!) {
    deletarFaseGrupoPartidas(id: $id)
  }
`;

export async function criarFaseGrupoPartidas(input: {
  nome: string;
  ordem?: number;
  faseCategoriaTorneioId: string;
}): Promise<{ id: string }> {
  const { data } = await apolloClient.mutate({
    mutation: CRIAR_FASE_GRUPO_PARTIDAS,
    variables: { input },
  });
  return data?.criarFaseGrupoPartidas;
}

export async function atualizarFaseGrupoPartidas(id: string, input: {
  nome?: string;
  ordem?: number;
  faseCategoriaTorneioId: string;
}): Promise<{ id: string }> {
  const { data } = await apolloClient.mutate({
    mutation: ATUALIZAR_FASE_GRUPO_PARTIDAS,
    variables: { id, input },
  });
  return data?.atualizarFaseGrupoPartidas;
}

export async function deletarFaseGrupoPartidas(id: string): Promise<boolean> {
  const { data } = await apolloClient.mutate({
    mutation: DELETAR_FASE_GRUPO_PARTIDAS,
    variables: { id },
  });
  return data?.deletarFaseGrupoPartidas;
}

export default {
  fetchFasesGrupoPartidasByFaseCategoriaTorneioId,
  buscarFaseGrupoPartidasPorFaseCategoriaTorneio,
};



